/* Collection page: filtering, price slider, grid density switch, mobile
   filter drawer.

   Filtering goes through the Section Rendering API rather than a full page
   load, so the sidebar, the product count and the grid all come back in one
   response and stay consistent with each other. Every listener is delegated
   from document, because the section's markup is replaced wholesale on each
   update and directly-bound handlers would not survive that. For the same
   reason the drawer's open state and the chosen sort are held here rather
   than read back off the DOM — the elements carrying them are destroyed on
   every update. */
(function () {
  'use strict';

  var VIEW_KEY = 'wl-coll-view';
  var PHONE = '(max-width: 749px)';
  var NARROW = '(max-width: 989px)';

  var drawerOpen = null;
  var lastNarrow = null;
  var currentSort = null;

  function root() { return document.querySelector('[data-cl-root]'); }
  function section() {
    var r = root();
    return r ? r.closest('.shopify-section') : null;
  }
  function is(q) { return window.matchMedia(q).matches; }

  /* ---------- Grid density ---------- */
  /* The view switch writes --cl-cols inline on the section, and an inline
     custom property outranks the mobile media query that sets the phone
     column count. So below 750px the inline value is removed instead of
     written, letting the stylesheet win. */
  function applyView() {
    var r = root(), sec = section();
    if (!r || !sec) return;
    var alt = localStorage.getItem(VIEW_KEY) === 'alt';
    if (is(PHONE)) {
      sec.style.removeProperty('--cl-cols');
    } else {
      var cols = alt ? r.getAttribute('data-cols-alt') : r.getAttribute('data-cols');
      if (cols) sec.style.setProperty('--cl-cols', cols);
    }
    r.querySelectorAll('[data-cl-view]').forEach(function (b) {
      b.setAttribute('aria-pressed', (b.getAttribute('data-cl-view') === 'alt') === alt ? 'true' : 'false');
    });
  }

  /* ---------- Sort ----------
     Two controls share name="sort_by": the toolbar <select> and the drawer's
     radio list. Submitting both would put two sort_by values in the form, so
     whichever does not belong to the current breakpoint is disabled.

     currentSort is the single source of truth. Copying straight from one
     control to the other instead would let the stale one win — on a wide
     screen the hidden radios still hold the previously applied sort, and
     writing that back over the select silently undid the shopper's choice.
     It is reset to null after each re-render so the value comes from the
     freshly rendered markup, which is what the server actually applied. */
  function syncSort() {
    var r = root();
    if (!r) return;
    var sel = r.querySelector('[data-cl-sort-select]');
    var radios = r.querySelectorAll('[data-cl-sort-radio]');
    if (!sel || !radios.length) return;

    if (currentSort === null) currentSort = sel.value;

    var narrow = is(NARROW);
    sel.value = currentSort;
    sel.disabled = narrow;
    radios.forEach(function (i) {
      i.checked = (i.value === currentSort);
      i.disabled = !narrow;
    });
  }

  /* ---------- Drawer / sidebar ----------
     One flag drives both breakpoints: below 990px it is the slide-in drawer,
     above it is whether the static sidebar is showing. */
  function applyDrawer() {
    var r = root();
    if (!r) return;
    var layout = r.querySelector('[data-cl-layout]');
    var side = r.querySelector('.cl__side');
    /* No filters configured — Liquid already marked the layout collapsed and
       there is nothing to open. */
    if (!layout || !side) return;

    var narrow = is(NARROW);
    if (drawerOpen === null) drawerOpen = !narrow;

    layout.classList.toggle('is-open', drawerOpen);
    layout.classList.toggle('is-collapsed', !narrow && !drawerOpen);

    var toggle = r.querySelector('[data-cl-toggle]');
    if (toggle) toggle.setAttribute('aria-expanded', drawerOpen ? 'true' : 'false');

    document.body.style.overflow = (narrow && drawerOpen) ? 'hidden' : '';
  }

  function setDrawer(open) {
    drawerOpen = open;
    applyDrawer();
  }

  /* ---------- Price slider ---------- */
  function num(el) { return parseFloat(el && el.value) || 0; }

  function paintSlider() {
    var r = root();
    if (!r) return;
    var lo = r.querySelector('[data-cl-min]');
    var hi = r.querySelector('[data-cl-max]');
    var fill = r.querySelector('[data-cl-fill]');
    if (!lo || !hi || !fill) return;
    var max = parseFloat(hi.max) || 1;
    var a = Math.min(num(lo), num(hi));
    var b = Math.max(num(lo), num(hi));
    fill.style.left = (a / max * 100) + '%';
    fill.style.width = ((b - a) / max * 100) + '%';
  }

  /* Keep the two thumbs from crossing, and mirror whichever control moved
     into its partner so the form always submits a sane range. */
  function syncFromRange(which) {
    var r = root();
    var lo = r.querySelector('[data-cl-min]');
    var hi = r.querySelector('[data-cl-max]');
    var loV = r.querySelector('[data-cl-minv]');
    var hiV = r.querySelector('[data-cl-maxv]');
    if (!lo || !hi) return;
    if (which === 'min' && num(lo) > num(hi)) lo.value = hi.value;
    if (which === 'max' && num(hi) < num(lo)) hi.value = lo.value;
    /* Blank rather than 0 / the cap, so an untouched end of the range does not
       add a redundant param to the URL. */
    if (loV) loV.value = num(lo) > 0 ? lo.value : '';
    if (hiV) hiV.value = num(hi) < parseFloat(hi.max) ? hi.value : '';
    paintSlider();
  }

  function syncFromField() {
    var r = root();
    var lo = r.querySelector('[data-cl-min]');
    var hi = r.querySelector('[data-cl-max]');
    var loV = r.querySelector('[data-cl-minv]');
    var hiV = r.querySelector('[data-cl-maxv]');
    if (!lo || !hi) return;
    var max = parseFloat(hi.max) || 0;
    var a = loV && loV.value !== '' ? Math.max(0, Math.min(num(loV), max)) : 0;
    var b = hiV && hiV.value !== '' ? Math.max(0, Math.min(num(hiV), max)) : max;
    if (a > b) a = b;
    lo.value = a;
    hi.value = b;
    paintSlider();
  }

  /* ---------- Fetch + swap ---------- */
  var pending = null;

  function refresh() {
    applyView();
    syncSort();
    applyDrawer();
    paintSlider();
  }

  function update() {
    var r = root();
    var form = r && r.querySelector('[data-cl-form]');
    if (!r || !form) return;
    /* Before reading the form, not only on breakpoint events — otherwise a
       missed resize would leave both sort controls enabled. */
    syncSort();

    var id = r.getAttribute('data-section-id');
    var base = r.getAttribute('data-url') || window.location.pathname;

    var params = new URLSearchParams();
    new FormData(form).forEach(function (v, k) {
      if (v !== null && String(v).trim() !== '') params.append(k, v);
    });
    var qs = params.toString();

    if (pending) pending.abort();
    pending = new AbortController();
    r.classList.add('cl__busy');

    fetch(base + '?' + qs + (qs ? '&' : '') + 'section_id=' + id, { signal: pending.signal })
      .then(function (res) { if (!res.ok) throw new Error('HTTP ' + res.status); return res.text(); })
      .then(function (html) {
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var next = doc.querySelector('[data-cl-root]');
        if (!next) throw new Error('no [data-cl-root] in response');
        r.innerHTML = next.innerHTML;
        window.history.replaceState({}, '', base + (qs ? '?' + qs : ''));
        /* Take the sort from the response rather than from memory, so the UI
           can never disagree with what the server applied. */
        currentSort = null;
        refresh();
      })
      .catch(function (err) {
        if (err && err.name === 'AbortError') return;
        /* Fall back to a normal navigation rather than leaving the shopper
           looking at results that do not match the controls. */
        console.error('[collection]', err);
        form.submit();
      })
      .then(function () { r.classList.remove('cl__busy'); });
  }

  var timer = null;
  function debounced() {
    clearTimeout(timer);
    timer = setTimeout(update, 320);
  }

  /* ---------- Wiring ---------- */
  document.addEventListener('input', function (e) {
    var r = root();
    if (!r || !r.contains(e.target)) return;
    if (e.target.matches('[data-cl-min]')) { syncFromRange('min'); return; }
    if (e.target.matches('[data-cl-max]')) { syncFromRange('max'); return; }
    if (e.target.matches('[data-cl-minv], [data-cl-maxv]')) { syncFromField(); debounced(); }
  });

  document.addEventListener('change', function (e) {
    var r = root();
    if (!r || !r.contains(e.target)) return;

    /* Record the choice before update() runs, whichever control was used. */
    if (e.target.matches('[data-cl-sort-select], [data-cl-sort-radio]')) {
      currentSort = e.target.value;
    }

    if (e.target.matches('[data-cl-min], [data-cl-max]')) { syncFromRange(e.target.matches('[data-cl-min]') ? 'min' : 'max'); update(); return; }
    if (e.target.matches('[data-cl-minv], [data-cl-maxv]')) { syncFromField(); update(); return; }
    if (e.target.closest('[data-cl-form]')) update();
  });

  document.addEventListener('click', function (e) {
    var r = root();
    if (!r) return;

    var view = e.target.closest('[data-cl-view]');
    if (view && r.contains(view)) {
      e.preventDefault();
      localStorage.setItem(VIEW_KEY, view.getAttribute('data-cl-view'));
      applyView();
      return;
    }

    var toggle = e.target.closest('[data-cl-toggle]');
    if (toggle && r.contains(toggle)) {
      e.preventDefault();
      setDrawer(!drawerOpen);
      return;
    }

    var close = e.target.closest('[data-cl-close]');
    if (close && r.contains(close)) {
      e.preventDefault();
      setDrawer(false);
      return;
    }

    var clear = e.target.closest('[data-cl-clear]');
    if (clear && r.contains(clear)) {
      e.preventDefault();
      var form = r.querySelector('[data-cl-form]');
      if (!form) return;
      form.querySelectorAll('input[type="checkbox"]').forEach(function (i) { i.checked = false; });
      form.querySelectorAll('[data-cl-minv], [data-cl-maxv]').forEach(function (i) { i.value = ''; });
      syncFromField();
      update();
    }
  });

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Escape') return;
    if (drawerOpen && is(NARROW)) setDrawer(false);
  });

  /* Submitting with Enter in a number field would do a full page load. */
  document.addEventListener('submit', function (e) {
    var f = e.target.closest('[data-cl-form]');
    if (!f) return;
    e.preventDefault();
    update();
  });

  /* ---------- Viewport ----------
     Only a genuine breakpoint crossing resets the open flag — a sidebar left
     open on a wide screen must not reappear as an open drawer on a narrow one.
     A plain resize must not, because the on-screen keyboard fires resize when
     a shopper taps a price field, and that would slam the drawer shut. */
  function onViewport() {
    var narrow = is(NARROW);
    if (narrow !== lastNarrow) {
      lastNarrow = narrow;
      drawerOpen = !narrow;
    }
    refresh();
  }

  [PHONE, NARROW].forEach(function (q) {
    var mq = window.matchMedia(q);
    if (mq.addEventListener) mq.addEventListener('change', onViewport);
    else if (mq.addListener) mq.addListener(onViewport);
  });

  /* Belt and braces: some environments resize the viewport without dispatching
     matchMedia change. The crossing check above keeps this cheap and safe. */
  var rt = null;
  window.addEventListener('resize', function () {
    clearTimeout(rt);
    rt = setTimeout(onViewport, 150);
  });

  function init() {
    lastNarrow = is(NARROW);
    refresh();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', function () {
    drawerOpen = null;
    currentSort = null;
    init();
  });
})();
